import { useState, useRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import CalendarIcon from '@app/assets/icons/calendar-01.svg?react';
import ChevronRightIcon from '@app/assets/icons/chevron-right.svg?react';
import ChevronDownIcon from '@app/assets/icons/chevron-down.svg?react';

interface CustomDatePickerProps {
  name: string;
  label?: string;
  containerClass?: string;
  preventPastDates?: boolean;
  position?: 'left' | 'right';
  sendImmediately?: boolean;
}

export const CustomDatePicker = ({
  name,
  label,
  containerClass,
  preventPastDates = false,
  position = 'right',
  sendImmediately = false,
}: CustomDatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevSendImmediatelyRef = useRef(sendImmediately);

  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const watchedValue = watch(name);

  useEffect(() => {
    // Only set date when sendImmediately changes from false to true
    if (sendImmediately && !prevSendImmediatelyRef.current) {
      const today = dayjs();
      setSelectedDate(today);
      setCurrentDate(today);
      setValue(name, today.format('YYYY-MM-DD'));
      // Force a re-render by updating the input value
      if (inputRef.current) {
        inputRef.current.value = today.format('DD MMM, YYYY');
      }
    }
    prevSendImmediatelyRef.current = sendImmediately;
  }, [sendImmediately, name, setValue]);

  useEffect(() => {
    // Handle watchedValue changes - always set selectedDate if there's a value
    if (watchedValue) {
      setSelectedDate(dayjs(watchedValue));
    }
  }, [watchedValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDateSelect = (date: dayjs.Dayjs) => {
    if (preventPastDates && date.isBefore(dayjs(), 'day')) {
      return;
    }
    setSelectedDate(date);
    setValue(name, date.format('YYYY-MM-DD'));
    setIsOpen(false);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(currentDate.subtract(1, 'month'));
  };

  const goToNextMonth = () => {
    setCurrentDate(currentDate.add(1, 'month'));
  };

  const getDaysInMonth = () => {
    const startOfMonth = currentDate.startOf('month');
    const endOfMonth = currentDate.endOf('month');
    const startOfWeek = startOfMonth.startOf('week');
    const endOfWeek = endOfMonth.endOf('week');

    const days = [];
    let day = startOfWeek;

    while (day.isBefore(endOfWeek) || day.isSame(endOfWeek, 'day')) {
      days.push(day);
      day = day.add(1, 'day');
    }

    return days;
  };

  const days = getDaysInMonth();
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  return (
    <div className={`w-full relative ${containerClass ?? ''}`}>
      {label && (
        <label htmlFor={name} className="font-medium text-sm inline-block mb-1">
          {label}
        </label>
      )}
      
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          readOnly
          value={selectedDate ? selectedDate.format('DD MMM, YYYY') : ''}
          data-selected={selectedDate ? selectedDate.format('DD MMM, YYYY') : 'empty'}
          onClick={() => setIsOpen(!isOpen)}
          className="bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 cursor-pointer"
          placeholder="Select date"
          key={selectedDate ? selectedDate.format('YYYY-MM-DD') : 'empty'}
        />
        <CalendarIcon 
          className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" 
        />
      </div>

      {isOpen && (
        <div
          ref={pickerRef}
          className={`absolute top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[280px] ${
            position === 'left' ? 'left-0' : 'right-0'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <button
              onClick={goToPreviousMonth}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRightIcon className="w-4 h-4 rotate-180" />
            </button>
            
            <div className="flex items-center gap-1">
              <span className="font-medium text-sm">
                {currentDate.format('MMM YYYY')}
              </span>
              <ChevronDownIcon className="w-3 h-3 text-gray-500" />
            </div>
            
            <button
              onClick={goToNextMonth}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Days of week */}
          <div className="grid grid-cols-7 gap-1 p-4 pb-2">
            {weekDays.map((day, index) => (
              <div
                key={day}
                className={`text-xs font-medium text-center py-2 ${
                  index === 0 ? 'text-brand-baseBlue' : 'text-gray-600'
                }`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1 px-4 pb-4">
            {days.map((day, index) => {
              const isCurrentMonth = day.month() === currentDate.month();
              const isSelected = selectedDate && day.isSame(selectedDate, 'day');
              const isToday = day.isSame(dayjs(), 'day');
              const isPast = day.isBefore(dayjs(), 'day');

              return (
                <button
                  key={index}
                  onClick={() => handleDateSelect(day)}
                  disabled={preventPastDates && isPast}
                  className={`
                    w-8 h-8 text-sm rounded-md flex items-center justify-center
                    ${isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                    ${isSelected ? 'bg-brand-baseBlue text-white' : ''}
                    ${isToday && !isSelected ? 'bg-gray-100 text-gray-900' : ''}
                    ${!isCurrentMonth ? 'text-gray-300' : ''}
                    ${preventPastDates && isPast ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer'}
                  `}
                >
                  {day.date()}
                </button>
              );
            })}
          </div>

          {/* Selected date display */}
          {selectedDate && (
            <div className="px-4 py-2 rounded-b-lg">
              <span className="text-xs text-gray-500 text-center block">
                {selectedDate.format('DD MMM, YYYY')}
              </span>
            </div>
          )}
        </div>
      )}

      {name && errors[name] && (
        <p role="alert" className="text-system-error text-sm text-left pt-1">
          {typeof errors?.[name]?.message === 'string'
            ? errors[name]?.message
            : ''}
        </p>
      )}
    </div>
  );
};
